import { getWebApplicationVersion, useRouter } from 'common-react';
import { Location } from 'history';
import { ProductWizard } from 'product-wizard';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect, Route, Switch } from 'react-router';
import {
  SkipLink,
  SkipLinkIdentifier,
  useSkipLinkContext,
  PageNotFound,
  Header,
  HeaderType,
  useIsMobile
} from 'ui-components';
import MobileHideOnScrollHeaderDrawer from '../MobileHideOnScrollHeaderDrawer/MobileHideOnScrollHeaderDrawer';
import { NavLink } from '../../translation/translation';

import useStyles from './RegistrationProcess.style';

function scrollToTop() {
  window.scrollTo(0, 0);
}
const RegistrationProcessStandardLayout: React.FC = ({ children }) => {

  const c = useStyles({});
  const isMobile = useIsMobile('sm');
  const { mainRef } = useSkipLinkContext();
  const [t] = useTranslation();
  const [url] = useTranslation('url');
  const thankYouPageUrl = `/${url('data-wizard')}/${url('thank-you')}`;

  const headerProps = {
    headerType: HeaderType.Creator,
    renderLogo: (logo: JSX.Element, linkClassName: string) => (
      <Switch>
        <Route exact path={thankYouPageUrl}>
          <a href="https://ingtfi24.pl/" className={linkClassName}>
            {logo}
          </a>
        </Route>
        <Route>
          <NavLink className={linkClassName} to="/" isActive={() => false}>
            {logo}
          </NavLink>
        </Route>
      </Switch>
    )
  };

  return (
    <div className={c.root}>
      <SkipLink skipToMainLinkLabel={t('skipLinks.skipToMainContent')}/>
      {isMobile ? (
        <MobileHideOnScrollHeaderDrawer>
          <Header {...headerProps} data-testid={'header'}/>
        </MobileHideOnScrollHeaderDrawer>
      ) : (
        <header data-testid={'header'}>
          <Header {...headerProps} />
        </header>
      )}
      <main role="main" ref={mainRef} id={SkipLinkIdentifier.MAIN}>
        {children}
      </main>
      <span className={c.version}>{getWebApplicationVersion()}</span>
    </div>
  );
};

const RegistrationProcess: React.FC = () => {
  const [url] = useTranslation('url');

  const { history, location } = useRouter();

  const redirectToThankYouPage = (locationParam: Location) => {
    const thankYouPageUrl = `/${url('data-wizard')}/${url('thank-you')}`;
    if (locationParam.pathname === thankYouPageUrl) {
      return;
    }
    const integrationStateString = sessionStorage.getItem('dataWizardState.integrationState');
    if (!integrationStateString) {
      return;
    }
    const integrationState: { status: number; } = JSON.parse(integrationStateString);
    const integrationStarted = integrationState.status !== 0;
    if (!integrationStarted) {
      return;
    }

    history.replace(thankYouPageUrl);
  };

  useEffect(() => {
    const unlisten  = history.listen((locationParam: Location) => {
      redirectToThankYouPage(locationParam);
      scrollToTop();
    });
    return unlisten ;
  }, [history]);

  redirectToThankYouPage(location);

  const urlToProductWizard = `/${url('product-wizard')}`;

  return (
    <Switch>
      <Route exact path="/" render={() => <Redirect to={urlToProductWizard}/>} />
      <Route path={[`/${url('product-wizard')}`, `/${url('data-wizard')}`, '/verification/:uuid']}>
        <RegistrationProcessStandardLayout>
          <Route path={urlToProductWizard} component={ProductWizard} />
        </RegistrationProcessStandardLayout>
      </Route>
      <Route>
        <RegistrationProcessStandardLayout>
          <PageNotFound/>
        </RegistrationProcessStandardLayout>
      </Route>
    </Switch>
  );
};

export default RegistrationProcess;
